/** de-CH formatting helpers for the App Usage page (UI itself stays English). */

const num = new Intl.NumberFormat('de-CH');
const chf = new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtNum = (n: number): string => num.format(n);
export const fmtChf = (n: number): string => chf.format(n);

/** Millions of tokens with one decimal, e.g. 12.3 M. */
export const fmtMTok = (n: number): string =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)} M` : num.format(n);

/**
 * The API sends UTC timestamps without an offset (SQL CONVERT style 126). `new Date()` reads a
 * date-time without offset as local time, which put every time on this page 1–2 h early — so a
 * missing offset is read as UTC here. A plain date is UTC in JavaScript already.
 */
export function parseUtc(iso: string): Date {
    const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso);
    return new Date(hasOffset || !iso.includes('T') ? iso : `${iso}Z`);
}

export function fmtDateTime(iso: string | null): string {
    if (!iso) return '—';
    const d = parseUtc(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('de-CH', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
    });
}

export function fmtDate(iso: string | null): string {
    if (!iso) return '—';
    const d = parseUtc(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
}

/** Days since an ISO timestamp (UTC), or null. */
export function daysSince(iso: string | null): number | null {
    if (!iso) return null;
    const d = parseUtc(iso);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000));
}

/** Traffic-light badge for "last used": green <30d, amber 30-90d, red >90d/never. */
export function lastUsedBadgeClass(iso: string | null): string {
    const days = daysSince(iso);
    if (days === null || days > 90) return 'bg-red-100 text-red-700';
    if (days >= 30) return 'bg-amber-100 text-amber-700';
    return 'bg-green-100 text-green-700';
}
